/**
 * @tt/lobby/records — Jukebox records registry + the pure queue-mutation
 * core (Phase B of docs/planning/Own-Tavern-Plan.md § "Phase B spec:
 * jukebox music queue").
 *
 * A "record" is a collectible that gates a requestable song. Each maps to
 * one `@tt/audio` Mood id (the procedural bed the clip plays). Ownership
 * lives in the `records` inventory bucket (bare semantic ids, like masks /
 * outfits); the cosmetics catalog id is `record-${id}` (see
 * cosmetics-catalog.ts + cosmetic-resolver.ts).
 *
 * This file also owns the JUKEBOX STATE SHAPE and its pure mutation
 * functions (`applyJukeboxRequest` / `applyJukeboxAdvance`). The ws-router
 * Lambda can't hold timers or be unit-tested directly, so all the queue
 * decisions live here as pure functions the Lambda calls (and node tests
 * exercise). The Lambda supplies auth (host check) + ownership; this module
 * has zero AWS / DOM / audio dependencies.
 */

/** A record's mood id is a `@tt/audio` `Mood`. Typed as string here so this
 *  package stays free of the @tt/audio dependency (which pulls in the Web
 *  Audio engine). The client casts it back to `Mood` at the playMoodClip
 *  call site; every value below is verified against @tt/audio MOODS. */
export interface RecordDef {
  /** Bare semantic id — matches the `records` inventory bucket value and
   *  the `record-${id}` catalog id. */
  id: string;
  label: string;
  /** A `@tt/audio` Mood id — the procedural bed the 12s clip plays. */
  mood: string;
  rarity: 1 | 2 | 3 | 4 | 5;
}

/**
 * The 10 records. 3 free defaults (everyone can always request SOMETHING),
 * 5 cheap embers-shop, 2 achievement-earned. Moods are the most iconic /
 * energetic beds in @tt/audio's MOODS table (a spread of driving,
 * triumphant, and tense so the queue never feels samey). Keep ids/moods in
 * sync with the `record-*` entries in cosmetics-catalog.ts + the embers
 * listings in shop.ts.
 */
export const RECORDS: readonly RecordDef[] = [
  // ── Free defaults (3) ──
  { id: 'tap-anthem',     label: 'Tap Anthem',     mood: 'tap-race',      rarity: 1 },
  { id: 'plinko-jangle',  label: 'Plinko Jangle',  mood: 'plinko-tavern', rarity: 1 },
  { id: 'victory-march',  label: 'Victory March',  mood: 'final',         rarity: 2 },
  // ── Embers-shop (5) ──
  { id: 'hex-pulse',      label: 'Hex Pulse',      mood: 'hexagon-heat',    rarity: 2 },
  { id: 'spire-ascent',   label: 'Spire Ascent',   mood: 'climb-spire',     rarity: 3 },
  { id: 'cellar-groove',  label: 'Cellar Groove',  mood: 'cellar-sweep',    rarity: 3 },
  { id: 'splatter-bop',   label: 'Splatter Bop',   mood: 'splatter-square', rarity: 2 },
  { id: 'coin-shuffle',   label: 'Coin Shuffle',   mood: 'coin-conveyer',   rarity: 3 },
  // ── Achievement-earned (2) ──
  { id: 'burnstile-drive', label: 'Burnstile Drive', mood: 'burnstile',  rarity: 4 },
  { id: 'draft-fanfare',   label: 'Draft Fanfare',   mood: 'draft-duel', rarity: 4 },
];

/** The 3 free records every character owns (source.kind === 'default' in the
 *  catalog). Used to seed the ownership set so nobody is locked out of the
 *  jukebox on a fresh account. */
export const DEFAULT_RECORD_IDS: readonly string[] = ['tap-anthem', 'plinko-jangle', 'victory-march'];

const RECORD_BY_ID = new Map(RECORDS.map((r) => [r.id, r]));

export function getRecord(id: string): RecordDef | null {
  return RECORD_BY_ID.get(id) ?? null;
}

/** The @tt/audio Mood id for a record, or null if the id is unknown. */
export function moodForRecord(id: string): string | null {
  return RECORD_BY_ID.get(id)?.mood ?? null;
}

// ── Jukebox queue state ────────────────────────────────────────────────

export interface JukeboxEntry {
  charId: string;
  name: string;
  recordId: string;
  /** Epoch ms the entry joined the queue — FIFO order key; preserved across
   *  a same-person re-request (replace record, keep slot). */
  at: number;
}

export interface JukeboxPlaying {
  charId: string;
  name: string;
  recordId: string;
  /** Epoch ms the clip started for EVERYONE — clients compute their
   *  play offset as `Date.now() - startedAt`. */
  startedAt: number;
}

export interface JukeboxState {
  queue: JukeboxEntry[];
  playing?: JukeboxPlaying;
}

/** Table max — the queue never holds more than 8 pending picks. */
export const JUKEBOX_QUEUE_MAX = 8;
/** Each pick plays this long for everyone. */
export const JUKEBOX_CLIP_MS = 12_000;
/** Host may advance once the current clip has played at least this long
 *  (anti-skip: stops a host spamming through everyone's picks instantly). */
export const JUKEBOX_MIN_ADVANCE_MS = 10_000;
/** Host-asleep fallback: ANY player may advance once the clip has overrun
 *  this long, so a queue can't wedge on an AFK/disconnected host. */
export const JUKEBOX_STALE_ADVANCE_MS = 20_000;
/** A client joining in progress SKIPS playing a clip whose offset already
 *  exceeds this (it's essentially over — avoid a 200ms blip). */
export const JUKEBOX_STALE_JOIN_MS = 12_500;

export function emptyJukebox(): JukeboxState {
  return { queue: [] };
}

/** True when `id` is a real record and `owned` contains it. */
export function recordOwned(id: string, owned: ReadonlySet<string>): boolean {
  return RECORD_BY_ID.has(id) && owned.has(id);
}

/**
 * Apply a `jukebox.request`. Rules (owner spec):
 *   - If nothing is playing, the pick promotes straight to `playing`
 *     (startedAt = now) — the requester hears it immediately, everyone
 *     joins on the next broadcast.
 *   - Otherwise it joins the FIFO queue. ONE pending entry per person: a
 *     re-request from someone already queued REPLACES their record but
 *     KEEPS their original `at` (their slot position doesn't reset).
 *   - Queue is capped at `JUKEBOX_QUEUE_MAX`; a request that would overflow
 *     is rejected (returns `changed:false`, caller broadcasts nothing).
 *
 * Pure: ownership + record-existence are validated by the caller BEFORE
 * this runs (see `recordOwned`). Returns a fresh state (never mutates the
 * input) plus whether anything changed (drives the broadcast decision).
 */
export function applyJukeboxRequest(
  state: JukeboxState | undefined,
  req: { charId: string; name: string; recordId: string },
  now: number,
): { state: JukeboxState; changed: boolean } {
  const queue = [...(state?.queue ?? [])];
  const playing = state?.playing;

  // Nothing playing → promote immediately (invariant: playing empty ⟺
  // queue empty, so there's no line to jump).
  if (!playing) {
    return {
      state: { queue: [], playing: { ...req, startedAt: now } },
      changed: true,
    };
  }

  const existingIdx = queue.findIndex((e) => e.charId === req.charId);
  if (existingIdx >= 0) {
    // Same person re-requests: swap the record, keep the slot + `at`.
    const prev = queue[existingIdx]!;
    if (prev.recordId === req.recordId) return { state: { queue, playing }, changed: false };
    queue[existingIdx] = { ...prev, recordId: req.recordId };
    return { state: { queue, playing }, changed: true };
  }

  if (queue.length >= JUKEBOX_QUEUE_MAX) {
    return { state: { queue, playing }, changed: false };
  }
  queue.push({ charId: req.charId, name: req.name, recordId: req.recordId, at: now });
  return { state: { queue, playing }, changed: true };
}

/**
 * Apply a `jukebox.advance`: pop the head of the queue into `playing`
 * (startedAt = now), or clear `playing` if the queue is empty. Pure — the
 * host/elapsed gate is `jukeboxAdvanceAllowed`, checked by the caller.
 */
export function applyJukeboxAdvance(
  state: JukeboxState | undefined,
  now: number,
): { state: JukeboxState; changed: boolean } {
  const queue = [...(state?.queue ?? [])];
  const next = queue.shift();
  if (next) {
    return {
      state: { queue, playing: { charId: next.charId, name: next.name, recordId: next.recordId, startedAt: now } },
      changed: true,
    };
  }
  // Queue empty — clear playing (if there was one). No-op if already idle.
  const wasPlaying = state?.playing !== undefined;
  return { state: { queue: [] }, changed: wasPlaying };
}

/**
 * Whether an advance from `isHost` at `now` is permitted:
 *   - nothing playing → always (harmless promote / no-op)
 *   - host → clip has played ≥ JUKEBOX_MIN_ADVANCE_MS (anti-skip)
 *   - non-host → clip has OVERRUN JUKEBOX_STALE_ADVANCE_MS (host-asleep
 *     fallback so an AFK host can't wedge the queue)
 */
export function jukeboxAdvanceAllowed(
  state: JukeboxState | undefined,
  isHost: boolean,
  now: number,
): boolean {
  const playing = state?.playing;
  if (!playing) return true;
  const elapsed = now - playing.startedAt;
  if (isHost) return elapsed >= JUKEBOX_MIN_ADVANCE_MS;
  return elapsed > JUKEBOX_STALE_ADVANCE_MS;
}
